import React, { useState, useEffect } from 'react';
import NavBar from './components/NavBar';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RulesPage from './features/rules/RulesPage';
import ProfilePage from './pages/ProfilePage';
import MyGamesPage from './features/games/MyGamesPage';
import GameDetailsPage from './features/gameDetails/GameDetailsPage';
import LeagueDetailsPage from './features/miniLeagues/LeagueDetailsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnsubscribedPage from './pages/UnsubscribedPage';
import PrivacyPage from './pages/PrivacyPage';
import SponsorPage from './pages/SponsorPage';
import SiteFooter from './components/SiteFooter';
import HouseBoardPage from './features/house/HouseBoardPage';
import { launchParams, clearQuery } from './lib/launchParams';
import * as authApi from './api/auth';
import './App.css';

/** Only these can be reached from an email link. Anything else falls through to home. */
const LINKABLE_PAGES = ['home', 'my-games', 'my-leagues', 'profile', 'rules', 'house', 'privacy', 'sponsor'];

/**
 * Of those, the ones that render nothing without a session.
 *
 * The House board is on this list even though it is the same board for everybody: the
 * endpoint behind it requires a session, so a link followed on a device that is not
 * signed in would otherwise land on a blank screen rather than the login form.
 */
const SIGNED_IN_PAGES = ['my-games', 'profile', 'house', 'sponsor'];

/**
 * Leagues and games are one page now, but `?page=my-leagues` is in the wild: two of the
 * notification emails the backend sends link there, and so does anything a player has
 * bookmarked. Those links keep working rather than falling through to home.
 */
const PAGE_ALIASES = { 'my-leagues': 'my-games' };
const resolvePage = (page) => PAGE_ALIASES[page] || page;

function App() {
  /**
   * The URL gets a say in the first render, not the second.
   *
   * Lazy initialiser rather than an effect on purpose: an effect would paint the home
   * page first and replace it, which on a password reset link looks exactly like the
   * link not working. See lib/launchParams - it is read once, at module load, before
   * anything here runs.
   */
  const [currentPage, setCurrentPage] = useState(() => {
    if (launchParams.unsubscribed) return 'unsubscribed';
    if (launchParams.resetToken) return 'reset-password';
    if (LINKABLE_PAGES.includes(launchParams.page)) return resolvePage(launchParams.page);
    return 'home';
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [viewingGameDetails, setViewingGameDetails] = useState(false);
  // Which tab the game screen should open on. Null lets it decide for itself.
  const [gameInitialTab, setGameInitialTab] = useState(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [viewingLeagueDetails, setViewingLeagueDetails] = useState(false);
  // A home-page action card the destination page should open on arrival.
  const [pendingAction, setPendingAction] = useState(null);
  /**
   * A join code typed on the register form, on its way to the join box.
   *
   * The landing page's two calls to action both end up on the register form, because
   * joining anything needs an account first - but the one that says "I've got a code"
   * used to arrive there with the intent thrown away, so somebody sent a code by a mate
   * signed up and then had to go and find the join box themselves. The code rides
   * through registration instead and opens that box already filled in.
   *
   * It is carried and never checked on the way: resolving a code needs a session, by
   * design, so there is nothing this side of the sign-up that could tell a good one from
   * a typo. `JoinGameModal` resolves it afterwards and reports a bad one exactly as it
   * does for a player who has been here for months.
   */
  const [pendingJoinCode, setPendingJoinCode] = useState('');
  // Whether the register form should put the cursor in its code field. What makes the
  // landing page's two buttons different once they have both landed on the same form.
  const [wantsJoinCode, setWantsJoinCode] = useState(false);

  // The unsubscribe already happened server-side before the redirect landed here, so
  // the parameter has done its job and can come out of the address bar. A reset token
  // stays until it is spent - ResetPasswordPage clears that one itself.
  useEffect(() => {
    if (launchParams.unsubscribed) {
      clearQuery();
    }
  }, []);

  // Restore the session on mount so a page refresh doesn't log the user out.
  useEffect(() => {
    authApi.getCurrentPlayer()
      .then((player) => {
        if (player) {
          setCurrentUser(player.player || player);
          setIsLoggedIn(true);
        }
      })
      .catch(() => {
        // no live session — stay logged out
      });
  }, []);

  const handleAuthSuccess = (player, joinCode) => {
    setCurrentUser(player);
    setIsLoggedIn(true);
    setWantsJoinCode(false);

    // Registered with a code in hand: straight to the join box rather than to a home
    // page they have no reason to read yet.
    if (joinCode) {
      setPendingJoinCode(joinCode);
      setPendingAction('join-game');
      setCurrentPage('my-games');
      return;
    }

    setCurrentPage('home');
  };

  const handleLogout = () => {
    authApi.logout().catch(() => {
      // best effort — clear client state regardless
    });
    setIsLoggedIn(false);
    setCurrentPage('login');
    setCurrentUser(null);
  };

  const handleNavigate = (pageId) => {
    setCurrentPage(resolvePage(pageId));
  };

  // All four home-page action cards open their flow on My Games, which owns both kinds
  // now. Signed-out visitors register first.
  const handleHomeAction = (actionId) => {
    if (!isLoggedIn) {
      setWantsJoinCode(actionId === 'join-game');
      setCurrentPage('register');
      return;
    }
    setPendingAction(actionId);
    setViewingGameDetails(false);
    setViewingLeagueDetails(false);
    setCurrentPage('my-games');
  };

  const handleViewGame = (gameId, initialTab = null) => {
    setViewingLeagueDetails(false);
    setSelectedLeagueId(null);
    setSelectedGameId(gameId);
    setGameInitialTab(initialTab);
    setViewingGameDetails(true);
  };

  const handleBackToGames = () => {
    setViewingGameDetails(false);
    setSelectedGameId(null);
    setGameInitialTab(null);
  };

  const handleViewLeague = (leagueId) => {
    setViewingGameDetails(false);
    setSelectedGameId(null);
    setSelectedLeagueId(leagueId);
    setViewingLeagueDetails(true);
  };

  const handleBackToLeagues = () => {
    setViewingLeagueDetails(false);
    setSelectedLeagueId(null);
  };

  // Opening something straight from the home page's waiting-on-you list. Both set the
  // page as well as the selection, because unlike the in-page handlers these are
  // crossing from Home into a section rather than moving within one.
  const handleOpenGameFromHome = (gameId) => {
    handleViewGame(gameId);
    setCurrentPage('my-games');
  };

  const handleOpenLeagueFromHome = (leagueId) => {
    handleViewLeague(leagueId);
    setCurrentPage('my-games');
  };

  return (
    // The phone layout puts navigation in a bottom bar, which is fixed to the viewport
    // and would otherwise sit over the end of every page.
    <div className={`app${isLoggedIn ? ' has-bottom-nav' : ''}`}>
      <NavBar
        isLoggedIn={isLoggedIn}
        currentUser={currentUser}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
      />

      {currentPage === 'home' && (
        <HomePage
          isLoggedIn={isLoggedIn}
          onNavigate={handleNavigate}
          onAction={handleHomeAction}
          onOpenGame={handleOpenGameFromHome}
          onOpenLeague={handleOpenLeagueFromHome}
          onOpenHouseBoard={() => setCurrentPage('house')}
        />
      )}

      {/* The platform-wide board, in full. Reached from the panel on the home page and
          from a link, which is why it is a page rather than an expander on the panel. */}
      {isLoggedIn && currentPage === 'house' && (
        <HouseBoardPage onBack={() => setCurrentPage('home')} />
      )}

      {/*
        * The three screens an email link can land on, all outside the isLoggedIn guard.
        * That is the point of them: somebody following a reset link has by definition
        * failed to log in, and somebody clicking unsubscribe from a mail client arrives
        * with no cookie at all. Guarding these would make every token-bearing link the
        * platform sends land on a page the recipient cannot see.
        */}
      {currentPage === 'unsubscribed' && (
        <UnsubscribedPage isLoggedIn={isLoggedIn} onNavigate={handleNavigate} />
      )}

      {currentPage === 'reset-password' && launchParams.resetToken && (
        <ResetPasswordPage
          token={launchParams.resetToken}
          onDone={() => setCurrentPage('forgot-password')}
          onSwitchToLogin={() => setCurrentPage('login')}
        />
      )}

      {currentPage === 'forgot-password' && (
        <ForgotPasswordPage onSwitchToLogin={() => setCurrentPage('login')} />
      )}

      {!isLoggedIn && currentPage === 'login' && (
        <LoginPage
          onAuthSuccess={handleAuthSuccess}
          onSwitchToRegister={() => setCurrentPage('register')}
          onForgotPassword={() => setCurrentPage('forgot-password')}
        />
      )}

      {/*
        * An email link naming a page behind the login, followed by somebody who is not
        * signed in on this device. Without this the guarded blocks below all render
        * nothing and the link lands on a blank screen - which is a real arrival now
        * that links come from a mailbox rather than from inside the app.
        */}
      {!isLoggedIn && SIGNED_IN_PAGES.includes(currentPage) && (
        <LoginPage
          onAuthSuccess={handleAuthSuccess}
          onSwitchToRegister={() => setCurrentPage('register')}
          onForgotPassword={() => setCurrentPage('forgot-password')}
        />
      )}

      {!isLoggedIn && currentPage === 'register' && (
        <RegisterPage
          onAuthSuccess={handleAuthSuccess}
          onSwitchToLogin={() => setCurrentPage('login')}
          onOpenPrivacy={() => setCurrentPage('privacy')}
          focusJoinCode={wantsJoinCode}
        />
      )}

      {/*
        * One section, three screens: the list, a game opened from it, a league opened
        * from it. The two detail views are mutually exclusive - opening either clears
        * the other - so the order here only settles what happens if that ever slipped.
        */}
      {isLoggedIn && currentPage === 'my-games' && viewingLeagueDetails && (
        <LeagueDetailsPage
          leagueId={selectedLeagueId}
          currentUser={currentUser}
          onBack={handleBackToLeagues}
          onViewGame={handleViewGame}
        />
      )}

      {isLoggedIn && currentPage === 'my-games' && !viewingLeagueDetails && viewingGameDetails && (
        <GameDetailsPage
          gameId={selectedGameId}
          currentUser={currentUser}
          initialTab={gameInitialTab}
          onBack={handleBackToGames}
          onOpenSponsor={() => setCurrentPage('sponsor')}
        />
      )}

      {isLoggedIn && currentPage === 'my-games' && !viewingLeagueDetails && !viewingGameDetails && (
        <MyGamesPage
          currentUser={currentUser}
          onViewGame={handleViewGame}
          onViewLeague={handleViewLeague}
          initialAction={pendingAction}
          initialJoinCode={pendingJoinCode}
          onActionConsumed={() => {
            setPendingAction(null);
            setPendingJoinCode('');
          }}
        />
      )}

      {currentPage === 'rules' && <RulesPage />}

      {/* Signed in or out, and linkable: somebody deciding whether to sign up is exactly who
          needs to read it. */}
      {currentPage === 'privacy' && <PrivacyPage />}

      {/* Where every advert leads. Signed in only, because only signed-in players see the
          adverts (lmsbe backlog item 52). */}
      {isLoggedIn && currentPage === 'sponsor' && <SponsorPage />}

      {isLoggedIn && currentPage === 'profile' && <ProfilePage currentUser={currentUser} />}

      <SiteFooter onNavigate={handleNavigate} />
    </div>
  );
}

export default App;
